import { useState } from "react";
import { toast } from "react-toastify";
import { FiCheck, FiCornerUpLeft } from "react-icons/fi";
import useAsync from "../../hooks/useAsync";
import { Card, PageHeader, DataTable, PersonCell, Button, Tabs, Modal, FormField, ErrorState, Banner } from "../../components/common";
import { listProgressApprovals, decideProgress } from "../../services/progressApprovalApi";
import { getErrorMessage } from "../../utils/errors";
import { formatDate, formatDateTime } from "../../utils/format";

export default function ProgressApprovalsPage() {
  const [tab, setTab] = useState("PENDING");
  const [type, setType] = useState("ALL"); // [NEW] All / GOAL / KRA / KPI
  const list = useAsync(() => listProgressApprovals({ view: "review", status: tab }), [tab]);
  const [deciding, setDeciding] = useState(null);
  const [busy, setBusy] = useState(false);

  const decide = async () => {
    setBusy(true);
    try {
      await decideProgress(deciding.row.id, deciding.decision, deciding.comment);
      toast.success(deciding.decision === "APPROVE" ? `Approved. ${deciding.row.itemTitle} is now at ${deciding.row.progress ?? "the reported"}%.` : "Returned to the employee with your comment.");
      setDeciding(null);
      list.reload();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (list.error) return <ErrorState message={list.error} onRetry={list.reload} />;

  return (
    <div className="page">
      <PageHeader title="Progress approvals" subtitle="Progress updates on goals, KRAs and KPIs from the people you review. The progress changes only when you approve the update; return it with a comment if something needs to change." />
      <Tabs tabs={[{ key: "PENDING", label: "Waiting for you" }, { key: "APPROVED", label: "Approved" }, { key: "RETURNED", label: "Returned" }]} value={tab} onChange={setTab} />
      <Tabs tabs={[{ key: "ALL", label: "All" }, { key: "GOAL", label: "Goals" }, { key: "KRA", label: "KRAs" }, { key: "KPI", label: "KPIs" }]} value={type} onChange={setType} />
      <Card padded={false}>
        <DataTable
          label="Progress updates"
          rows={(list.data || []).filter((r) => type === "ALL" || r.itemType === type)}
          loading={list.loading && !list.data}
          emptyTitle={tab === "PENDING" ? "No progress updates waiting" : "Nothing here yet"}
          columns={[
            { key: "employee", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={r.employeeId} /> },
            { key: "item", label: "Goal / KRA / KPI", render: (r) => <span className="cell-stack"><strong>{r.itemTitle}</strong><small>{r.itemType}{r.isCorrection ? ", correction of an earlier update" : ""}</small></span> },
            {
              key: "progress", label: "Reported",
              render: (r) => (
                <span className="cell-stack">
                  <strong>{r.progress ?? "—"}%{r.completed ? " · marked completed" : ""}</strong>
                  <small>as of {formatDate(r.asOf)}; currently {r.effectiveProgress ?? 0}%</small>
                  {r.status === "PENDING" && !r.becomesCurrent && (
                    <small>A newer update is already approved; approving this one only adds it to the history.</small>
                  )}
                  {r.status === "PENDING" && r.becomesCurrent && r.progress !== null && r.progress < (r.effectiveProgress ?? 0) && (
                    <small className="text-warning">Approving lowers the current progress.</small>
                  )}
                </span>
              ),
            },
            { key: "comment", label: "Employee note", render: (r) => r.comment || <span className="muted">—</span> },
            {
              key: "decision", label: tab === "PENDING" ? "Submitted" : "Decision",
              render: (r) => (tab === "PENDING" ? formatDateTime(r.submittedAt) : (
                <span className="cell-stack"><span>{r.decidedBy}, {formatDateTime(r.decidedAt)}</span>{r.decisionComment && <small>{r.decisionComment}</small>}</span>
              )),
            },
            {
              key: "act", label: "", align: "right",
              render: (r) => r.status === "PENDING" && (
                <span className="row-actions">
                  <Button size="sm" icon={FiCheck} onClick={() => setDeciding({ row: r, decision: "APPROVE", comment: "" })}>Approve</Button>
                  <Button size="sm" variant="secondary" icon={FiCornerUpLeft} onClick={() => setDeciding({ row: r, decision: "RETURN", comment: "" })}>Return</Button>
                </span>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        open={Boolean(deciding)}
        size="sm"
        title={deciding?.decision === "APPROVE" ? "Approve progress update" : "Return progress update"}
        subtitle={deciding ? `${deciding.row.employeeName}: ${deciding.row.itemTitle}` : ""}
        onClose={busy ? undefined : () => setDeciding(null)}
        footer={<><Button variant="ghost" onClick={() => setDeciding(null)} disabled={busy}>Cancel</Button><Button onClick={decide} loading={busy}>{deciding?.decision === "APPROVE" ? "Approve" : "Return"}</Button></>}
      >
        {deciding && (
          <div className="form-stack">
            {deciding.decision === "APPROVE" && deciding.row.completed && (
              <Banner tone="info">Approving marks this goal as completed.</Banner>
            )}
            <FormField label="Comment for the employee" hint={deciding.decision === "RETURN" ? "Required: what should they change or add?" : "Optional."}>
              <textarea rows={3} value={deciding.comment} onChange={(e) => setDeciding((d) => ({ ...d, comment: e.target.value }))} />
            </FormField>
          </div>
        )}
      </Modal>
    </div>
  );
}
